import { Bot, CalendarSync, PhoneCall } from 'lucide-react-native';
import type { ComponentType } from 'react';
import { Badge } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import type { CalendarEventSource } from '../../../types/calendar';

export type EventSourceIndicatorSize = 'sm' | 'md';

export interface EventSourceIndicatorProps {
  /** CalendarEvent.source. Missing/undefined is treated the same as 'manual'. */
  source: CalendarEventSource | null | undefined;
  /** 'sm' fits a month-grid chip or a day-list row; 'md' fits a detail preview. */
  size?: EventSourceIndicatorSize;
  /**
   * Drops the visible label, keeping only the icon — for contexts too tight
   * even for 'sm' (a dense month-grid cell). The accessible label never
   * shrinks: a screen-reader user always hears the full phrase.
   */
  iconOnly?: boolean;
  testID?: string;
}

type AutomatedSource = Exclude<CalendarEventSource, 'manual'>;

interface SourceVisuals {
  icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  color: 'purple' | 'blue' | 'neutral';
  iconColor: string;
  labelKey: string;
  accessibilityLabelKey: string;
}

// 'manual' is intentionally absent from this map — see EventSourceIndicator
// below. Every other source pairs an icon with a translated label AND a
// separate, fuller accessibilityLabel, so the signal never rides on colour
// alone (scripts/check-design-system.mjs + project WCAG AA standard).
const SOURCE_VISUALS: Record<AutomatedSource, SourceVisuals> = {
  voice_agent: {
    icon: PhoneCall,
    color: 'purple',
    iconColor: colors.accent.purple[300],
    labelKey: 'calendar.eventSource.voiceAgent.label',
    accessibilityLabelKey: 'calendar.eventSource.voiceAgent.accessibilityLabel',
  },
  platform_agent: {
    icon: Bot,
    color: 'blue',
    iconColor: colors.accent.blue[300],
    labelKey: 'calendar.eventSource.platformAgent.label',
    accessibilityLabelKey: 'calendar.eventSource.platformAgent.accessibilityLabel',
  },
  // Not created by a human in this org NOR by one of our agents — pulled in
  // from an external provider calendar (e.g. Google). Left unbadged it would
  // read as a colleague's manual entry, which is exactly the ambiguity AC6
  // exists to remove, so it gets its own (non-agent) indicator too.
  provider_sync: {
    icon: CalendarSync,
    color: 'neutral',
    iconColor: colors.neutral[300],
    labelKey: 'calendar.eventSource.providerSync.label',
    accessibilityLabelKey: 'calendar.eventSource.providerSync.accessibilityLabel',
  },
};

function isAutomatedSource(
  source: CalendarEventSource | null | undefined,
): source is AutomatedSource {
  return source != null && source !== 'manual';
}

/**
 * The indicator's accessible phrase, for callers that cannot nest it.
 *
 * A parent `Pressable` with its own `accessibilityLabel` overrides its
 * children's labels for screen readers, so an indicator rendered inside one is
 * visually present but silent. Those callers fold this phrase into their own
 * label instead, and keep the icon purely visual. Returns null for 'manual' and
 * for a missing source, matching the component.
 */
export function useEventSourceAccessibilityLabel(
  source: CalendarEventSource | null | undefined,
): string | null {
  const { t } = useTranslation();

  if (!isAutomatedSource(source)) return null;

  return t(SOURCE_VISUALS[source].accessibilityLabelKey);
}

// Answers "who put this on my calendar?" without colour being the only
// signal. 'manual' (and a missing/legacy source) renders nothing: an
// unmarked event reads as "a person did this", which is both the majority
// case and the quietest default — less visual noise than badging every
// single event on the board.
export function EventSourceIndicator({
  source,
  size = 'sm',
  iconOnly = false,
  testID,
}: Readonly<EventSourceIndicatorProps>) {
  const { t } = useTranslation();

  if (!isAutomatedSource(source)) {
    return null;
  }

  const visuals = SOURCE_VISUALS[source];
  const Icon = visuals.icon;
  const accessibilityLabel = t(visuals.accessibilityLabelKey);

  return (
    <Badge
      testID={testID}
      variant="subtle"
      color={visuals.color}
      size={size}
      icon={<Icon size={12} color={visuals.iconColor} strokeWidth={2} />}
      accessibilityLabel={accessibilityLabel}
    >
      {iconOnly ? null : t(visuals.labelKey)}
    </Badge>
  );
}
